// src/components/EditEventDialog.tsx
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControlLabel,
  Switch,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  Box,
  Typography,
  Divider,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { updateEvent, uploadEventImage, deleteEventImage } from '../services/eventService';
import { Event, UpdateEventPayload } from '../types/church';
import { ImageUpload } from './common';

interface EditEventDialogProps {
  open: boolean;
  onClose: () => void;
  event: Event | null;
  onSuccess: () => void;
}

const EditEventDialog: React.FC<EditEventDialogProps> = ({
  open,
  onClose,
  event,
  onSuccess,
}) => {
  const { t } = useTranslation();

  // Form state
  const [titre, setTitre] = useState('');
  const [description, setDescription] = useState('');
  const [date, setDate] = useState('');
  const [isRecurring, setIsRecurring] = useState(false);
  const [recurrencePattern, setRecurrencePattern] = useState<'daily' | 'weekly' | 'monthly' | 'yearly' | null>(null);

  // Image state (managed separately from form, direct upload)
  const [imageUrl, setImageUrl] = useState<string | null>(null);

  // UI state
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // Initialize form when event changes
  useEffect(() => {
    if (event) {
      setTitre(event.titre || '');
      setDescription(event.description || '');
      // Convert ISO date to datetime-local format
      const eventDate = new Date(event.date);
      const localDate = new Date(eventDate.getTime() - eventDate.getTimezoneOffset() * 60000)
        .toISOString()
        .slice(0, 16);
      setDate(localDate);
      setIsRecurring(event.is_recurring || false);
      setRecurrencePattern(event.recurrence_pattern || null);
      // Initialize image URL
      setImageUrl(event.image_url || null);
    }
  }, [event]);

  const handleSubmit = async () => {
    if (!event) return;
    setError(null);

    // Validation
    if (!titre.trim()) {
      setError(t('admin.events.errors.titleRequired'));
      return;
    }

    if (!description.trim()) {
      setError(t('admin.events.errors.descriptionRequired'));
      return;
    }

    if (!date) {
      setError(t('admin.events.errors.dateRequired'));
      return;
    }

    try {
      setLoading(true);
      const payload: UpdateEventPayload = {};

      // Only include changed fields
      if (titre.trim() !== event.titre) payload.titre = titre.trim();
      if (description.trim() !== event.description) payload.description = description.trim();

      const selectedDate = new Date(date);
      const originalDate = new Date(event.date);
      if (selectedDate.getTime() !== originalDate.getTime()) {
        payload.date = selectedDate.toISOString();
      }

      if (isRecurring !== event.is_recurring) payload.is_recurring = isRecurring;
      if (isRecurring && recurrencePattern !== event.recurrence_pattern) {
        payload.recurrence_pattern = recurrencePattern;
      }
      if (!isRecurring && event.is_recurring) {
        payload.is_recurring = false;
        payload.recurrence_pattern = null;
      }

      // Only call API if there are changes
      if (Object.keys(payload).length === 0) {
        onClose();
        return;
      }

      await updateEvent(event.id, payload);
      onSuccess();
    } catch (err: any) {
      console.error('Failed to update event:', err);
      setError(err.response?.data?.detail || t('admin.events.errorUpdating'));
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setError(null);
      onClose();
    }
  };

  // Image upload/delete handlers
  const handleImageUpload = async (file: File, onProgress: (progress: number) => void) => {
    if (!event) return;
    try {
      const result = await uploadEventImage(event.id, file, onProgress);
      setImageUrl(result.url);
    } catch (err: any) {
      console.error('Failed to upload event image:', err);
      throw new Error(err.response?.data?.detail || t('imageUpload.errorUpload'));
    }
  };

  const handleImageDelete = async () => {
    if (!event) return;
    try {
      await deleteEventImage(event.id);
      setImageUrl(null);
    } catch (err: any) {
      console.error('Failed to delete event image:', err);
      throw new Error(err.response?.data?.detail || t('imageUpload.errorDelete'));
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('admin.events.editTitle')}</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          {/* Image Section */}
          <Typography variant="subtitle1" fontWeight="bold">
            {t('admin.events.images.title')}
          </Typography>

          <ImageUpload
            currentImageUrl={imageUrl}
            onUpload={handleImageUpload}
            onDelete={handleImageDelete}
            maxSizeMB={10}
            label={t('admin.events.images.image')}
            helperText={t('admin.events.images.imageHelper')}
            disabled={loading}
            shape="rectangle"
            width="100%"
            height={200}
          />

          <Divider sx={{ my: 1 }} />

          <TextField
            label={t('admin.events.form.title')}
            value={titre}
            onChange={(e) => setTitre(e.target.value)}
            fullWidth
            required
            disabled={loading}
          />

          <TextField
            label={t('admin.events.form.description')}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            multiline
            rows={4}
            fullWidth
            required
            disabled={loading}
          />

          <TextField
            label={t('admin.events.form.dateTime')}
            type="datetime-local"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            fullWidth
            required
            disabled={loading}
            InputLabelProps={{
              shrink: true,
            }}
          />

          <FormControlLabel
            control={
              <Switch
                checked={isRecurring}
                onChange={(e) => setIsRecurring(e.target.checked)}
                disabled={loading}
              />
            }
            label={t('admin.events.form.recurring')}
          />

          {isRecurring && (
            <FormControl fullWidth>
              <InputLabel>{t('admin.events.form.recurrenceType')}</InputLabel>
              <Select
                value={recurrencePattern || ''}
                onChange={(e) => setRecurrencePattern(e.target.value as any)}
                label={t('admin.events.form.recurrenceType')}
                disabled={loading}
              >
                <MenuItem value="daily">{t('admin.events.recurrence.daily')}</MenuItem>
                <MenuItem value="weekly">{t('admin.events.recurrence.weekly')}</MenuItem>
                <MenuItem value="monthly">{t('admin.events.recurrence.monthly')}</MenuItem>
                <MenuItem value="yearly">{t('admin.events.recurrence.yearly')}</MenuItem>
              </Select>
            </FormControl>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          {t('common.cancel')}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading}
        >
          {loading ? t('admin.events.saving') : t('common.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditEventDialog;
